import classNames from 'classnames';
import React__default, { useId, useRef } from 'react';
import { Button } from '../atoms/Button.js';
import { Divider } from '../atoms/Divider.js';
import { toSpacingAttributes } from '../layouts/Spacing/spacing.js';
import { Accordion, AccordionHeader, AccordionBody } from './Accordion.js';
import './NavigationMenu.scss';

/**
 * NavigationMenu - A flexible vertical menu component
 *
 * Renders menu items as a vertical list. Accepts button items and group items.
 * Groups are rendered as accordions with nested children.
 * Buttons can be wrapped in trigger components (PopupTrigger, SheetTrigger, etc.)
 *
 * @example
 * ```tsx
 * <NavigationMenu
 *   items={[
 *     {
 *       type: 'button',
 *       button: (
 *         <PopupTrigger>
 *           <NavigationMenuButton icon={<ShopIcon />}>Shop</NavigationMenuButton>
 *           <Popup>...</Popup>
 *         </PopupTrigger>
 *       ),
 *     },
 *     {
 *       type: 'group',
 *       title: 'Learn',
 *       icon: <InfoIcon />,
 *       items: [
 *         <NavigationMenuButton>Buying online via Ekho</NavigationMenuButton>,
 *         <NavigationMenuButton>Our protection plans</NavigationMenuButton>,
 *       ],
 *     },
 *     {
 *       type: 'button',
 *       button: <NavigationMenuButton icon={<OrderIcon />}>Orders</NavigationMenuButton>,
 *     },
 *   ]}
 * />
 * ```
 */
const NavigationMenu = ({ items, itemsSpacing = '2', className, ariaLabel = 'Menu', }) => {
    const baseId = useId();
    const menuRef = useRef(null);
    const handleKeyDown = (event) => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
            return;
        }
        const menuElement = menuRef.current;
        if (!menuElement) {
            return;
        }
        // Get all focusable buttons within the menu
        const buttons = Array.from(menuElement.querySelectorAll('button:not([disabled])'));
        if (buttons.length === 0) {
            return;
        }
        // Find currently focused button
        const currentIndex = buttons.findIndex((button) => button === document.activeElement);
        let nextIndex;
        if (currentIndex === -1) {
            // No button is focused, focus the first one
            nextIndex = 0;
        }
        else if (event.key === 'ArrowDown') {
            // Move to next button (wrap to start if at end)
            nextIndex = (currentIndex + 1) % buttons.length;
        }
        else {
            // Move to previous button (wrap to end if at start)
            nextIndex = currentIndex === 0 ? buttons.length - 1 : currentIndex - 1;
        }
        event.preventDefault();
        buttons[nextIndex]?.focus();
    };
    return (React__default.createElement("nav", { ref: menuRef, "aria-label": ariaLabel, "data-component": 'navigation-menu', className: className, onKeyDown: handleKeyDown, ...toSpacingAttributes('g', itemsSpacing) }, items.map((item, index) => {
        const key = `${baseId}-${index}`;
        if (item.type === 'button') {
            return React__default.createElement(React__default.Fragment, { key: key }, item.button);
        }
        if (item.type === 'divider') {
            return React__default.createElement(Divider, { key: key });
        }
        if (item.type === 'title') {
            return (React__default.createElement("h3", { className: 'navigation-menu-title--rev', key: key }, item.title));
        }
        // Group item
        return (React__default.createElement(Accordion, { key: key, isDisabled: item.isDisabled },
            React__default.createElement(AccordionHeader, { className: 'navigation-menu-accordion-button--rev', variant: 'ghost', icon: item.icon, ariaCurrent: item.isSelected ? 'page' : undefined }, item.title),
            React__default.createElement(AccordionBody, { className: 'navigation-menu-accordion-body--rev', itemsSpacing: itemsSpacing }, item.items.map((nestedItem, nestedIndex) => (React__default.createElement(React__default.Fragment, { key: `${key}-nested-${nestedIndex}` }, nestedItem))))));
    })));
};
/**
 * NavigationMenuButton - A standalone button component that can be used in NavigationMenu or wrapped in trigger components
 *
 * @example
 * ```tsx
 * // Direct usage
 * <NavigationMenuButton icon={<HomeIcon />} onPress={() => navigate('/')}>
 *   Home
 * </NavigationMenuButton>
 *
 * // Wrapped in a trigger
 * <PopupTrigger>
 *   <NavigationMenuButton icon={<HomeIcon />}>Shop</NavigationMenuButton>
 *   <Popup>...</Popup>
 * </PopupTrigger>
 * ```
 */
const NavigationMenuButton = (props) => {
    return (React__default.createElement(Button
    // eslint-disable-next-line react/jsx-props-no-spreading -- wrapper component, props forwarded to Button
    , { ...props, variant: props.variant ?? 'ghost', size: 'large', ariaCurrent: props.isSelected ? 'page' : undefined, className: classNames('navigation-menu-button--rev', props.className) },
        props.children,
        props.badge));
};

export { NavigationMenu, NavigationMenuButton };
//# sourceMappingURL=NavigationMenu.js.map
